import {
  faBolt,
  faChevronDown,
  faUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Children, useEffect, useState, type ReactNode } from 'react';
import {
  Anchor,
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton,
} from '../../../core';

/** How long an optimistic "Generating…" state is shown before falling back
 * to the ungenerated state. Generate is a fire-and-forget A2UI action
 * dispatch (no promise this component can await) — the reactive `result`
 * prop is the only success signal, so this is a safety net against a
 * dropped/erroring action leaving the button spinning forever. */
const GENERATE_TIMEOUT_MS = 15_000;

function splitDiffs(value?: string): string[] {
  return value
    ? value
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
}

interface WhatsDifferentProps {
  readonly diffs: readonly string[];
  readonly detailedDiffs?: readonly string[];
}

function WhatsDifferent({ diffs, detailedDiffs }: WhatsDifferentProps) {
  const [open, setOpen] = useState(false);
  const hasDetails = Boolean(detailedDiffs && detailedDiffs.length > 0);

  if (diffs.length === 0) return null;

  return (
    <Box>
      <Text size="xs" c="dimmed" mb={2}>
        What&apos;s different
      </Text>
      <Text size="xs" c="dimmed" lh={1.4}>
        {diffs.join(' · ')}
      </Text>
      {hasDetails && (
        <>
          <UnstyledButton
            onClick={() => setOpen((o) => !o)}
            mt={6}
            aria-expanded={open}
          >
            <Group gap={4} wrap="nowrap">
              <Text size="xs" fw={600} c="blue.6">
                {open ? 'Hide details' : 'Show details'}
              </Text>
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{
                  width: 10,
                  height: 10,
                  color: 'var(--mantine-color-blue-6)',
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 150ms ease',
                }}
              />
            </Group>
          </UnstyledButton>
          <Collapse in={open}>
            <Stack gap={4} mt={6}>
              {detailedDiffs?.map((line) => (
                <Group key={line} gap={8} wrap="nowrap" align="flex-start">
                  <Box
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'var(--mantine-color-blue-6)',
                      marginTop: 6,
                      flexShrink: 0,
                    }}
                  />
                  <Text size="xs" c="dimmed" lh={1.4}>
                    {line}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Collapse>
        </>
      )}
    </Box>
  );
}

/** Result of a successful Generate — populated reactively by the host after
 * the surface action resolves (see A2UISurfaceList's result-injection
 * branch), never sent by the agent up front. */
export interface QuoteGenerateResult {
  readonly newQuoteId: string;
  readonly newQuoteNumber: string;
  readonly premium: string;
  readonly product?: string;
}

/** Written into the same `/results/<id>` path as `QuoteGenerateResult` when
 * Generate fails, so the existing reactive binding also carries failure —
 * no second schema-bound prop needed. Discriminated from a real result by
 * the `error` flag (a `QuoteGenerateResult` never has one). */
export interface QuoteGenerateError {
  readonly error: true;
  readonly message?: string;
}

export interface QuoteOptionProps {
  readonly label: string;
  readonly limit: string;
  readonly retention: string;
  readonly price: string;
  /** Short, always-shown summary of what changed vs. the original, pipe-separated (e.g. "Limit raised to $2M | Retention unchanged"). */
  readonly diffs?: string;
  /** Optional longer breakdown, pipe-separated. When present, renders an expandable "Show details" toggle. */
  readonly detailedDiffs?: string;
  /** Standalone carve-out (ZSP): friendly, comma-separated labels of the coverage parts kept
   * on this option (e.g. "D&O" or "D&O, Crime"). Present only when this is a standalone
   * single-/multi-part copy that dropped the suite's other parts; omitted for a full-suite copy. */
  readonly coverages?: string;
  /** Preview outcome only ("OK" | "ERROR") — not a generation-lifecycle
   * state. Mirrors the real backend's `QuoteDraft.status`. */
  readonly status: 'OK' | 'ERROR';
  readonly errorReason?: string;
  /** Status label shown next to a generated option, e.g. "Referred". */
  readonly statusLabel?: string;
  readonly result?: QuoteGenerateResult | QuoteGenerateError;
  readonly onGenerate?: () => void;
  readonly onOpenReview?: () => void;
  /** Optional slot rendered beneath the option body — e.g. an inline Quote
   *  Note the agent attaches (via updateComponents) after this option
   *  generates. */
  readonly children?: ReactNode;
}

function isGenerateError(
  value: QuoteGenerateResult | QuoteGenerateError | undefined
): value is QuoteGenerateError {
  return Boolean(value) && (value as QuoteGenerateError).error === true;
}

// Requires `newQuoteId` (not just "not error-shaped") before treating a
// result as a real generated success — an incomplete/malformed success
// payload (e.g. `{}`) must not flip the row to email/review UI or count
// toward CopyQuote's "Email all" readiness with no actual quote behind it.
function isGenerateResult(
  value: QuoteGenerateResult | QuoteGenerateError | undefined
): value is QuoteGenerateResult {
  return (
    Boolean(value) &&
    !isGenerateError(value) &&
    Boolean((value as QuoteGenerateResult).newQuoteId)
  );
}

/** Every render/effect branch below reads exactly one of these four phases
 * instead of independently-derived booleans (`generating`/`generated`/
 * `generateFailed`). That combinatorial-boolean shape is what produced a
 * string of one-off bugs (stuck loading, retry showing stale error, an
 * empty payload counting as generated) — each fix corrected one wrong
 * *interaction* between flags computed separately, and the next one was
 * still there to find. A single derived state has no room for an
 * inconsistent combination to exist in the first place. */
export type GenerateState =
  | { readonly phase: 'idle' }
  | { readonly phase: 'generating' }
  | { readonly phase: 'success'; readonly result: QuoteGenerateResult }
  | { readonly phase: 'error'; readonly message?: string };

/** Pure derivation, exported so every phase transition — including the
 * priority between a live click and a stale settled result — is covered by
 * direct, combinatorial unit tests rather than only inferred from rendered
 * DOM assertions. */
export function deriveGenerateState(
  result: QuoteGenerateResult | QuoteGenerateError | undefined,
  generating: boolean
): GenerateState {
  // Optimistic `generating` (set synchronously on click, before the A2UI
  // action even dispatches) always wins over a settled `result` — retrying
  // after a failure or a malformed success doesn't clear the old value in
  // the data model until the NEW request resolves, so without this
  // priority the stale copy would show through the entire retry.
  if (generating) return { phase: 'generating' };
  if (isGenerateResult(result)) return { phase: 'success', result };
  if (result) {
    return {
      phase: 'error',
      message: isGenerateError(result) ? result.message : undefined,
    };
  }
  return { phase: 'idle' };
}

// Only called from the non-success render branch (see the ternary below),
// so `state.phase` here is never actually 'success' — that phase renders
// through a separate branch with its own copy.
function statusTextFor(
  state: Exclude<GenerateState, { phase: 'success' }>
): string {
  switch (state.phase) {
    case 'generating':
      return 'Generating quote…';
    case 'error':
      return state.message || "Couldn't generate quote — try again";
    case 'idle':
    default:
      return 'Not generated yet';
  }
}

/**
 * A single quote option card — one row in a Copy Quote comparison. Rendered
 * standalone as a CopyQuote child, one instance per option the agent lists.
 */
export function QuoteOption({
  label,
  limit,
  retention,
  price,
  diffs,
  detailedDiffs,
  coverages,
  status,
  errorReason,
  statusLabel,
  result,
  onGenerate,
  onOpenReview,
  children,
}: QuoteOptionProps) {
  const diffList = splitDiffs(diffs);
  const detailedDiffList = splitDiffs(detailedDiffs);
  const [generating, setGenerating] = useState(false);
  const state = deriveGenerateState(result, generating);

  useEffect(() => {
    // Any settled result — success or error — ends the optimistic spinner;
    // previously only a real result cleared it, so a failed generate sat in
    // "Generating…" for the full GENERATE_TIMEOUT_MS with no feedback.
    if (result) setGenerating(false);
  }, [result]);

  useEffect(() => {
    if (!generating) return;
    const id = setTimeout(() => setGenerating(false), GENERATE_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [generating]);

  const handleGenerate = () => {
    setGenerating(true);
    onGenerate?.();
  };

  if (status === 'ERROR') {
    return (
      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text fw={600} size="sm">
            {label}
          </Text>
          <Badge
            color="red"
            variant="light"
            size="sm"
            radius="sm"
            tt="none"
            fw={500}
          >
            Not available
          </Badge>
        </Group>
        <Text size="xs" c="dimmed" mt={4}>
          {errorReason || 'This option is not available.'}
        </Text>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb="sm">
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Group gap={8} wrap="nowrap" align="center">
            <Text fw={600} size="sm">
              {label}
            </Text>
            {coverages && (
              <Badge
                color="teal"
                variant="light"
                size="sm"
                radius="sm"
                tt="none"
                fw={500}
              >
                Standalone · {coverages}
              </Badge>
            )}
            {state.phase === 'success' && statusLabel && (
              <Badge
                color="orange"
                variant="light"
                size="sm"
                radius="sm"
                tt="none"
                fw={500}
              >
                {statusLabel}
              </Badge>
            )}
          </Group>
          {state.phase === 'success' ? (
            <Anchor size="xs" fw={500} onClick={() => onOpenReview?.()}>
              <Group gap={4} wrap="nowrap">
                {state.result.newQuoteNumber}
                <FontAwesomeIcon
                  icon={faUpRightFromSquare}
                  style={{ width: 10, height: 10 }}
                />
              </Group>
            </Anchor>
          ) : (
            <Text
              size="xs"
              c={state.phase === 'error' ? 'red.6' : 'dimmed'}
              fs="italic"
            >
              {statusTextFor(state)}
            </Text>
          )}
        </Stack>

        {state.phase !== 'success' && (
          <Group gap={8}>
            <Button
              variant="light"
              color="blue"
              radius="md"
              size="xs"
              onClick={handleGenerate}
              loading={state.phase === 'generating'}
              aria-label={`Generate quote for ${label}`}
              leftSection={
                <FontAwesomeIcon
                  icon={faBolt}
                  style={{ width: 14, height: 14 }}
                />
              }
            >
              Generate
            </Button>
          </Group>
        )}
      </Group>

      <SimpleGrid cols={3} spacing="sm" mb="sm">
        <Box>
          <Text size="xs" c="dimmed">
            Premium
          </Text>
          <Text fw={700} size="md">
            {state.phase === 'success' ? state.result.premium : price}
          </Text>
        </Box>
        <Box>
          <Text size="xs" c="dimmed">
            Limit
          </Text>
          <Text size="sm" fw={500}>
            {limit}
          </Text>
        </Box>
        <Box>
          <Text size="xs" c="dimmed">
            Deductible
          </Text>
          <Text size="sm" fw={500}>
            {retention}
          </Text>
        </Box>
      </SimpleGrid>

      <WhatsDifferent diffs={diffList} detailedDiffs={detailedDiffList} />

      {state.phase === 'success' && (
        <Button
          variant="light"
          color="blue"
          size="xs"
          mt="sm"
          fullWidth
          leftSection={
            <FontAwesomeIcon
              icon={faUpRightFromSquare}
              style={{ width: 12, height: 12 }}
            />
          }
          onClick={() => onOpenReview?.()}
        >
          Open UW review
        </Button>
      )}

      {/* Children.count (not `!= null`): the a2ui wrapper always passes the
          result of `.map()`, which is an empty (non-null) array when no note
          is attached — that must not render a margined, empty Box. */}
      {Children.count(children) > 0 && <Box mt="sm">{children}</Box>}
    </Paper>
  );
}
